"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

const AUTH_CARD_CLASSES =
  "w-full max-w-md border-muted/20 bg-transparent shadow-sm rounded-3xl overflow-hidden";
const AUTH_TITLE_CLASSES =
  "text-3xl font-black tracking-tighter uppercase text-foreground drop-shadow-sm";

const AUTH_SUBMIT_BUTTON_CLASSES = cn(
  "w-full rounded-full font-black uppercase tracking-widest text-xs h-12 shadow-md transition-all",
  "bg-primary text-primary-foreground"
);

export function PrihlasenieForm() {
  const EMAIL_LABEL = "Prihlasovací e-mail";
  const PASSWORD_LABEL = "Prístupové heslo";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") || "/";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error("Chyba pri prihlásení", {
        description: error.message,
      });
      setLoading(false);
      return;
    }

    toast.success("Úspešne prihlásený");
    setLoading(false);
    router.push(redirect);
    router.refresh();
  }

  return (
    <Card className={AUTH_CARD_CLASSES}>
      <CardHeader className="text-center space-y-2">
        <CardTitle className={AUTH_TITLE_CLASSES}>Login</CardTitle>
        <CardDescription>
          Prihláste sa pre hlasovanie o nominácii
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1"
            >
              {EMAIL_LABEL}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="vas@email.sk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 border-muted/20 bg-muted/5 focus:border-primary/50"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="password"
              title={PASSWORD_LABEL}
              className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1"
            >
              {PASSWORD_LABEL}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 border-muted/20 bg-muted/5 focus:border-primary/50 pr-12"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-12 px-3 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-6 pt-6 pb-10">
          <Button
            type="submit"
            className={AUTH_SUBMIT_BUTTON_CLASSES}
            disabled={loading}
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Prihlasujem...
              </>
            ) : (
              "Login"
            )}
          </Button>
          <div className="text-sm font-medium text-muted-foreground text-center">
            Nemáte účet?{" "}
            <Link
              href="/registracia"
              className="text-primary hover:underline font-bold"
            >
              Zaregistrujte sa
            </Link>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
